import { useCallback, useEffect, useState } from 'react'
import { ApiError, api } from '../api/client'
import type { Me } from '../api/types'

export type SessionState =
  | { status: 'loading' }
  | { status: 'signed-in'; me: Me }
  | { status: 'signed-out' }
  | { status: 'unreachable'; detail: string }

/**
 * Who the browser is signed in as.
 *
 * A 401 is not an error worth logging or showing: it means "show the sign-in
 * screen". So does an identity whose credential the upstream stopped taking,
 * because no room can be read until it signs in again. Anything else is a real failure and says so, because somebody
 * looking at a sign-in screen during an outage deserves to know it is the
 * server and not them.
 */
export function useSession() {
  const [state, setState] = useState<SessionState>({ status: 'loading' })

  const refresh = useCallback(async () => {
    try {
      const me = await api.me()
      setState(me.identities.some((identity) => identity.needs_sign_in)
        ? { status: 'signed-out' }
        : { status: 'signed-in', me })
    } catch (error) {
      if (error instanceof ApiError && error.isUnauthenticated) {
        setState({ status: 'signed-out' })
        return
      }
      setState({
        status: 'unreachable',
        detail: error instanceof Error ? error.message : 'unknown error',
      })
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  return { state, refresh }
}
